import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import type { Quotation } from '@tailoring-crm/shared';
import { useAuth } from '../../lib/auth/auth-context.js';
import { useCustomerQuotations } from '../../lib/quotations/use-quotations.js';
import { cn, Button, ListSkeleton } from '@tailoring-crm/ui';
import { QuotationFormDialog, type QuotationFormMode } from './quotation-form-dialog.js';
import { QuotationRow } from './quotation-row.js';
import { RejectQuotationDialog } from './reject-quotation-dialog.js';

/**
 * What this customer has been quoted, on the screen where somebody is about to ring them.
 *
 * "What did we say to them in July?" has to be answerable, so the document lives here, next to
 * the number and the follow-up date, as well as on the quotations screen — this is where it is
 * WORKED ON, and that list is where it is chased.
 *
 * A tailor sees none of it. Quotations are money, and the permission matrix refuses them every
 * route — rendering a card whose every button 403s would be worse than rendering nothing.
 */
export function QuotationCard({
  customerId,
  customerName,
  className,
}: {
  customerId: string;
  customerName: string;
  className?: string;
}): ReactElement | null {
  const { t } = useTranslation();
  const { role } = useAuth();

  const quotations = useCustomerQuotations(role === 'tailor' ? null : customerId);

  const [form, setForm] = useState<{ mode: QuotationFormMode; quotation: Quotation | null } | null>(
    null,
  );
  const [rejecting, setRejecting] = useState<Quotation | null>(null);

  if (role === 'tailor') return null;

  const view = quotations.data;
  const documents = view?.quotations ?? [];
  const registered = view?.registered ?? false;
  /** At most one draft is open at a time, and it is what the shop is working on. */
  const draft = documents.find((document) => document.state === 'draft');

  return (
    <section className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">{t('quotations.heading')}</h3>
          <p className="text-sm text-muted-foreground">
            {documents.length === 0 ? t('quotations.none') : t('quotations.hint')}
          </p>
        </div>

        {/* One draft at a time. A second open draft for the same customer is two prices nobody can
            tell apart, and the shop's own answer to "what did we quote?" becomes a guess. */}
        {!draft ? (
          <Button size="sm" onClick={() => setForm({ mode: 'create', quotation: null })}>
            <FileText aria-hidden="true" />
            {t('quotations.new')}
          </Button>
        ) : null}
      </div>

      {quotations.isPending ? <ListSkeleton rows={2} /> : null}

      {documents.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {documents.map((quotation) => (
            <QuotationRow
              key={quotation.id}
              quotation={quotation}
              onEdit={(document) => setForm({ mode: 'edit', quotation: document })}
              onRevise={(document) => setForm({ mode: 'revise', quotation: document })}
              onReject={setRejecting}
            />
          ))}
        </ul>
      ) : null}

      {form ? (
        <QuotationFormDialog
          open
          onOpenChange={(open) => !open && setForm(null)}
          mode={form.mode}
          party={{ kind: 'customer', id: customerId, name: customerName }}
          quotation={form.quotation}
          registered={registered}
          onSaved={() => setForm(null)}
        />
      ) : null}

      <RejectQuotationDialog
        quotation={rejecting}
        onOpenChange={(open) => !open && setRejecting(null)}
      />
    </section>
  );
}

export default QuotationCard;
